import React from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Client from '../Client';
import Config from '../ClientConfig';

const INSIGHT_TYPE_SALES_VALUE_BELOW_EXPECTED = 1001;
const INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED = 1002;
const INSIGHT_TYPE_SALES_VALUE_DAILY_SUMMARY = 1003;
const INSIGHT_TYPE_TRANSACTION_TREND = 1011;
const SALES_DOWN_REPORT_TIMEOUT_MS = 30000;
const SALES_DOWN_REPORT_SCHEMA_VERSION = 'kalopsia.sales-down-report/1.0.0';
const SALES_VALUE_REPORT_SCHEMA_VERSION = 'kalopsia.sales-value-report/1.0.0';
const SALES_DRIVER_ANALYSIS_SCHEMA_VERSION = 'kalopsia.sales-driver-analysis/1.0.0';
const PRODUCT_DRIVER_ANALYSIS_SCHEMA_VERSION = 'kalopsia.product-driver-analysis/1.0.0';

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}
function getLocalDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return "";
}

class IntelligenceInsights extends React.Component {
  constructor(props) {
    super(props);

    const defaultDate = getLocalDateString(addDays(new Date(), -1));

    this.state = {
      client: new Client("none"),
      insights: [],
      page: 0,
      rowsPerPage: 5,

      dateFrom: defaultDate,
      dateTo: defaultDate,
      status: "",
      severity: "",
      insightType: "",
      statusView: "open",

      selectedInsight: null,
      reviewNote: "",
      drawerOpen: false,
      salesDownReportOpen: false,
      salesDownReport: null,
      salesDownReportLoading: false,
      salesDownReportError: "",

      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
      showMobileFilters: false,
      filterData: {}
    };

    this.getData = this.getData.bind(this);
    this.doSync = this.doSync.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.applyDatePreset = this.applyDatePreset.bind(this);
    this.openInsight = this.openInsight.bind(this);
    this.closeInsightDrawer = this.closeInsightDrawer.bind(this);
    this.openSalesExplanation = this.openSalesExplanation.bind(this);
    this.closeSalesDownReport = this.closeSalesDownReport.bind(this);
    this.loadSalesDownReport = this.loadSalesDownReport.bind(this);
    this.handleReviewNoteChange = this.handleReviewNoteChange.bind(this);
    this.setInsightStatus = this.setInsightStatus.bind(this);
    this.closeSnackbar = this.closeSnackbar.bind(this);
    this.handleStatusViewChange = this.handleStatusViewChange.bind(this);
    this.listRef = React.createRef();
    this.salesDownReportRequest = null;
    this.salesDownReportTimer = null;
    this.salesDownReportRequestId = 0;
    this.isUnmounted = false;
  }

  componentDidMount() {
    // React 18 StrictMode performs a development-only simulated unmount/remount.
    // Re-arm async response handling for the remounted component instance.
    this.isUnmounted = false;
    this.getData();
    if (this.props.initialInsight) this.openInsight(this.props.initialInsight);
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.cancelSalesDownReportRequest();
  }

  getData() {
    let queryData = { include_ongoing_trends: true };

    if (this.state.dateFrom) {
      queryData.dateFrom = this.state.dateFrom;
    }
    if (this.state.dateTo) {
      queryData.dateTo = this.state.dateTo;
    }
    if (this.state.status !== "") {
      queryData.status = this.state.status;
    }
    if (this.state.severity !== "") {
      queryData.severity = this.state.severity;
    }
    if (this.state.insightType !== "") {
      queryData.insightType = this.state.insightType;
    }

    let data = this.state.client.getData(
      Config.API_URL_INTELLIGENCE_INSIGHTS,
      queryData
    );

    this.setState({
      insights: Array.isArray(data) ? data : [],
      filterData: queryData
    });
  }

  doSync() {
    this.getData();
  }

  handleFilterChange(event) {
    const { name, value } = event.target;
    const nextState = {
      [name]: value,
      page: 0
    };

    // When the user selects From, inspect that day first.
    if (name === "dateFrom") {
      nextState.dateTo = value;
    }
    // Keep the range valid if To is before From.
    if (name === "dateTo" && this.state.dateFrom && value < this.state.dateFrom) {
      nextState.dateFrom = value;
    }

    this.setState(
      nextState,
      () => this.getData()
    );
  }

  clearFilters() {
    const defaultDate = getLocalDateString(addDays(new Date(), -1));

    this.setState(
      {
        dateFrom: defaultDate,
        dateTo: defaultDate,
        status: "",
        severity: "",
        insightType: "",
        statusView: "open",
        page: 0
      },
      () => this.getData()
    );
  }

  handleStatusViewChange(statusView) {
    this.setState({
      statusView,
      status: "",
      page: 0
    });
  }

  applyDatePreset(preset) {
    const today = new Date();

    let dateFrom = getLocalDateString(addDays(today, -1));
    let dateTo = getLocalDateString(addDays(today, -1));

    if (preset === "today") {
      dateFrom = getLocalDateString(today);
      dateTo = getLocalDateString(today);
    }

    if (preset === "last7") {
      dateFrom = getLocalDateString(addDays(today, -7));
      dateTo = getLocalDateString(addDays(today, -1));
    }

    this.setState(
      {
        dateFrom,
        dateTo,
        page: 0
      },
      () => this.getData()
    );
  }

  pushApiData(url, queryData) {
    const client = this.state.client;

    if (!client || typeof client.pushData !== "function") {
      return {
        status: "ERROR",
        message: "Client pushData is not available."
      };
    }

    const originalPrefix = client.prefix;

    try {
      // For this page only:
      // force pushData to use the API URL exactly as provided.
      client.prefix = "";

      return client.pushData(url, queryData);
    } finally {
      client.prefix = originalPrefix;
    }
  }

  getSetStatusUrl(row) {
    const baseUrl = String(Config.API_URL_INTELLIGENCE_INSIGHTS || "").split("?")[0];

    const cleanBaseUrl = baseUrl.endsWith("/")
      ? baseUrl.slice(0, -1)
      : baseUrl;

    return `${cleanBaseUrl}/${row.iref}/set-status/`;
  }

  getSalesDownReportUrl(row) {
    const baseUrl = String(Config.API_URL_INTELLIGENCE_INSIGHTS || "").split("?")[0];
    const cleanBaseUrl = baseUrl.endsWith("/")
      ? baseUrl.slice(0, -1)
      : baseUrl;

    return `${cleanBaseUrl}/${row.iref}/sales-report/`;
  }

  isSalesDownInsight(row) {
    return Number(row && row.insight_type) === INSIGHT_TYPE_SALES_VALUE_BELOW_EXPECTED;
  }

  isValidSalesDownReport(report) {
    return Boolean(
      report &&
      typeof report === "object" &&
      !Array.isArray(report) &&
      (
        report.schema_version === SALES_DOWN_REPORT_SCHEMA_VERSION
        || report.schema_version === SALES_VALUE_REPORT_SCHEMA_VERSION
      ) &&
      report.result &&
      typeof report.result === "object" &&
      !Array.isArray(report.result)
    );
  }

  cancelSalesDownReportRequest() {
    this.salesDownReportRequestId += 1;

    if (this.salesDownReportTimer) {
      clearTimeout(this.salesDownReportTimer);
      this.salesDownReportTimer = null;
    }

    if (
      this.salesDownReportRequest &&
      typeof this.salesDownReportRequest.abort === "function"
    ) {
      this.salesDownReportRequest.abort();
    }

    this.salesDownReportRequest = null;
  }

  getSalesDownReportError(error) {
    const requestError = Array.isArray(error) ? error[0] : error;
    const status = Number(requestError && requestError.status);
    const backendMessage = requestError && requestError.responseJSON && requestError.responseJSON.detail;

    if (status === 401) {
      return "Your session is no longer authorized. Sign in again and retry.";
    }
    if (status === 403) {
      return "You do not have permission to view this sales report.";
    }
    if (status === 404) {
      return "A detailed report is not available for this insight. Legacy insights must be regenerated to include the structured sales explanation.";
    }
    if (status === 400) {
      return "This insight cannot provide a sales report.";
    }
    if (status === 0) {
      return "The report could not be reached. Check the connection and retry.";
    }

    if (backendMessage) {
      return String(backendMessage).substring(0, 220);
    }

    return "The sales report could not be loaded. Retry in a moment.";
  }

  openSalesExplanation() {
    const row = this.state.selectedInsight;
    const isSalesDown = this.isSalesDownInsight(row);

    if (!isSalesDown && Number(row && row.insight_type) !== INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED) {
      return;
    }

    this.cancelSalesDownReportRequest();
    this.setState(
      {
        salesDownReportOpen: true,
        salesDownReport: null,
        salesDownReportLoading: true,
        salesDownReportError: ""
      },
      this.loadSalesDownReport
    );
  }

  loadSalesDownReport() {
    const row = this.state.selectedInsight;
    const client = this.state.client;

    if (!this.isSalesDownInsight(row) && Number(row && row.insight_type) !== INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED) {
      this.setState({
        salesDownReportLoading: false,
        salesDownReportError: "This insight cannot provide a sales report."
      });
      return;
    }

    this.cancelSalesDownReportRequest();
    const requestId = this.salesDownReportRequestId;

    this.setState({
      salesDownReport: null,
      salesDownReportLoading: true,
      salesDownReportError: ""
    });

    if (!client || typeof client.getDataAsync !== "function") {
      this.setState({
        salesDownReportLoading: false,
        salesDownReportError: "The report service is not available in this session."
      });
      return;
    }

    let request;
    try {
      request = client.getDataAsync(this.getSalesDownReportUrl(row), {});
    } catch (error) {
      this.setState({
        salesDownReportLoading: false,
        salesDownReportError: this.getSalesDownReportError(error)
      });
      return;
    }

    this.salesDownReportRequest = request;
    this.salesDownReportTimer = setTimeout(() => {
      if (requestId !== this.salesDownReportRequestId || this.isUnmounted) {
        return;
      }

      this.salesDownReportRequestId += 1;
      this.salesDownReportTimer = null;
      if (request && typeof request.abort === "function") {
        request.abort();
      }
      this.salesDownReportRequest = null;
      this.setState({
        salesDownReportLoading: false,
        salesDownReportError: "The report request timed out. Retry in a moment."
      });
    }, SALES_DOWN_REPORT_TIMEOUT_MS);

    const handleSuccess = (report) => {
      if (requestId !== this.salesDownReportRequestId || this.isUnmounted) {
        return;
      }

      if (this.salesDownReportTimer) {
        clearTimeout(this.salesDownReportTimer);
        this.salesDownReportTimer = null;
      }
      this.salesDownReportRequest = null;

      if (!this.isValidSalesDownReport(report)) {
        this.setState({
          salesDownReportLoading: false,
          salesDownReportError: "The report response is incomplete or uses an unsupported format."
        });
        return;
      }

      this.setState({
        salesDownReport: report,
        salesDownReportLoading: false,
        salesDownReportError: ""
      });
    };

    const handleFailure = (error) => {
      if (requestId !== this.salesDownReportRequestId || this.isUnmounted) {
        return;
      }

      if (this.salesDownReportTimer) {
        clearTimeout(this.salesDownReportTimer);
        this.salesDownReportTimer = null;
      }
      this.salesDownReportRequest = null;
      this.setState({
        salesDownReportLoading: false,
        salesDownReportError: this.getSalesDownReportError(error)
      });
    };

    if (
      request &&
      typeof request.done === "function" &&
      typeof request.fail === "function"
    ) {
      request.done(handleSuccess);
      request.fail(handleFailure);
      return;
    }

    Promise.resolve(request).then(handleSuccess, handleFailure);
  }

  closeSalesDownReport() {
    this.cancelSalesDownReportRequest();
    this.setState({
      salesDownReportOpen: false,
      salesDownReport: null,
      salesDownReportLoading: false,
      salesDownReportError: ""
    });
  }

  openInsight(row) {
    this.setState({
      selectedInsight: row,
      reviewNote: row.review_note || "",
      drawerOpen: true
    });
  }

  closeInsightDrawer() {
    this.cancelSalesDownReportRequest();
    this.setState({
      drawerOpen: false,
      selectedInsight: null,
      reviewNote: "",
      salesDownReportOpen: false,
      salesDownReport: null,
      salesDownReportLoading: false,
      salesDownReportError: ""
    });
  }

  handleReviewNoteChange(event) {
    this.setState({
      reviewNote: event.target.value
    });
  }

  closeSnackbar() {
    this.setState({
      snackbarOpen: false
    });
  }

  setInsightStatus(statusValue) {
    const selectedInsight = this.state.selectedInsight;

    if (!selectedInsight) {
      return;
    }

    const payload = {
      status: statusValue,
      review_note: this.state.reviewNote || ""
    };
    const statusUrl = this.getSetStatusUrl(selectedInsight);
    const updatedInsight = this.pushApiData(
      statusUrl,
      payload
    );

    if (
      updatedInsight &&
      (
        updatedInsight.status === "ERROR" ||
        updatedInsight.detail ||
        updatedInsight.message
      )
    ) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: String(
          updatedInsight.detail || updatedInsight.message || "Could not update insight status."
        ).substring(0, 180),
        snackbarSeverity: "error"
      });

      return;
    }

    if (!updatedInsight || !updatedInsight.iref) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: "Status update did not return a valid insight.",
        snackbarSeverity: "error"
      });

      return;
    }

    const updatedInsights = (this.state.insights || []).map((row) => {
      if (row.iref === updatedInsight.iref) {
        return updatedInsight;
      }

      return row;
    });

    this.setState({
      insights: updatedInsights,
      selectedInsight: updatedInsight,
      reviewNote: updatedInsight.review_note || "",
      snackbarOpen: true,
      snackbarMessage: "Insight status updated.",
      snackbarSeverity: "success"
    });
  }

  handleChangePage = (event, newPage) => {
    this.setState(
      { page: newPage },
      () => this.scrollListToTop()
    );
  }

  scrollListToTop() {
    if (this.listRef && this.listRef.current) {
      this.listRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({
      rowsPerPage: +event.target.value,
      page: 0
    });
  }

  isDailySummary(row) {
    return Number(row && row.insight_type) === INSIGHT_TYPE_SALES_VALUE_DAILY_SUMMARY;
  }

  isTransactionTrend(row) {
    return Number(row && row.insight_type) === INSIGHT_TYPE_TRANSACTION_TREND;
  }

  renderTrendLink(row) {
    if (!this.isTransactionTrend(row) || !this.props.onOpenTrends) return null;
    return <Button size="small" sx={{ textTransform: 'none' }}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        this.props.onOpenTrends({ location: row.location, week_end: row.trend && row.trend.week_end, episode: row.iref });
      }}>View Trends</Button>;
  }

  getLocationLabel(row) {
    if (this.isDailySummary(row)) {
      return 'All locations';
    }
    return row.location_name || '-';
  }

  getInsightTypeLabel(row) {
    const insightType = Number(row && row.insight_type);

    if (this.isTransactionTrend(row)) return 'Transactions';

    if (insightType === INSIGHT_TYPE_SALES_VALUE_DAILY_SUMMARY) {
      return 'Daily summary';
    }
    if (insightType === INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED) {
      return 'Above expected';
    }
    if (insightType === INSIGHT_TYPE_SALES_VALUE_BELOW_EXPECTED) {
      return 'Below expected';
    }

    return 'Insight';
  }

  getSeverityColor(severity) {
    if (Number(severity) === 4) {
      return {
        color: '#7f1d1d',
        bgcolor: 'rgba(127, 29, 29, 0.12)',
        border: '1px solid rgba(127, 29, 29, 0.34)'
      };
    }

    if (Number(severity) === 3) {
      return {
        color: '#991b1b',
        bgcolor: 'rgba(239, 68, 68, 0.10)',
        border: '1px solid rgba(239, 68, 68, 0.22)'
      };
    }

    if (Number(severity) === 2) {
      return {
        color: '#92400e',
        bgcolor: 'rgba(245, 158, 11, 0.12)',
        border: '1px solid rgba(245, 158, 11, 0.26)'
      };
    }

    return {
      color: '#334155',
      bgcolor: 'rgba(100, 116, 139, 0.10)',
      border: '1px solid rgba(100, 116, 139, 0.20)'
    };
  }

  getStatusColor(status) {
    if (Number(status) === 0) {
      return {
        color: '#1a237e',
        bgcolor: 'rgba(26, 35, 126, 0.08)',
        border: '1px solid rgba(26, 35, 126, 0.14)'
      };
    }

    if (Number(status) === 2) {
      return {
        color: '#075985',
        bgcolor: 'rgba(14, 165, 233, 0.10)',
        border: '1px solid rgba(14, 165, 233, 0.20)'
      };
    }

    if (Number(status) === 3) {
      return {
        color: '#166534',
        bgcolor: 'rgba(34, 197, 94, 0.10)',
        border: '1px solid rgba(34, 197, 94, 0.22)'
      };
    }

    if (Number(status) === 4) {
      return {
        color: '#475569',
        bgcolor: 'rgba(100, 116, 139, 0.10)',
        border: '1px solid rgba(100, 116, 139, 0.18)'
      };
    }

    return {
      color: '#334155',
      bgcolor: 'rgba(100, 116, 139, 0.08)',
      border: '1px solid rgba(100, 116, 139, 0.16)'
    };
  }

  getDirection(row) {
    const insightType = Number(row.insight_type);
    const diff = Number(row.difference_percent || 0);

    if (insightType === INSIGHT_TYPE_SALES_VALUE_DAILY_SUMMARY) {
      return {
        label: 'Summary',
        color: '#1a237e',
        bg: 'rgba(26, 35, 126, 0.08)',
        border: '1px solid rgba(26, 35, 126, 0.16)'
      };
    }
    if (!this.isTransactionTrend(row) && (insightType === INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED || diff > 0)) {
      return {
        label: 'Opportunity',
        color: '#166534',
        bg: 'rgba(34, 197, 94, 0.10)',
        border: '1px solid rgba(34, 197, 94, 0.22)'
      };
    }

    return {
      label: 'Attention',
      color: '#991b1b',
      bg: 'rgba(239, 68, 68, 0.10)',
      border: '1px solid rgba(239, 68, 68, 0.22)'
    };
  }

  getStatusLabel(row) {
    if (row.status_label) {
      return row.status_label;
    }

    const statusMap = {
      0: 'New',
      1: 'Seen',
      2: 'In Review',
      3: 'Resolved',
      4: 'Ignored',
      9: 'Archived'
    };

    return statusMap[Number(row.status)] || '-';
  }

  getSeverityLabel(row) {
    if (row.severity_label) {
      return row.severity_label;
    }

    const severityMap = {
      1: 'Low',
      2: 'Medium',
      3: 'High',
      4: 'Critical'
    };

    return severityMap[Number(row.severity)] || 'Severity';
  }

  formatDate(value) {
    if (!value) {
      return "-";
    }

    return new Date(value).toLocaleDateString("ro-RO", {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  }

  formatMoney(value) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) {
      return "-";
    }

    return numberValue.toLocaleString("ro-RO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  formatMoneyDetailed(value) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) {
      return "-";
    }

    return numberValue.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  formatPercent(value) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) {
      return "-";
    }

    return `${numberValue > 0 ? '+' : ''}${numberValue.toFixed(1)}%`;
  }

  formatPercentMagnitude(value) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) {
      return "-";
    }

    return `${Math.abs(numberValue).toFixed(1)}%`;
  }

  formatFactor(value) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) {
      return "-";
    }

    return `${numberValue.toFixed(2)}×`;
  }

  formatTimestamp(value) {
    if (!value) {
      return "-";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return String(value);
    }

    return parsed.toLocaleString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  }

  formatCode(value) {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    return String(value)
      .split(".")
      .map((part) => part.replace(/_/g, " "))
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" — ");
  }

  formatBoolean(value) {
    if (value === true) {
      return "Yes";
    }
    if (value === false) {
      return "No";
    }
    return "-";
  }

  renderReportValue(label, value, options = {}) {
    const displayValue = value === null || value === undefined || value === ""
      ? "-"
      : value;

    return (
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.35}>
          <Typography
            sx={{
              color: '#64748b',
              fontSize: '0.68rem',
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
          {options.help ? (
            <Tooltip title={options.help} arrow>
              <InfoOutlinedIcon
                aria-label={`${label} information`}
                sx={{ color: '#94a3b8', fontSize: '0.85rem', cursor: 'help' }}
              />
            </Tooltip>
          ) : null}
        </Stack>
        <Typography
          sx={{
            mt: 0.3,
            color: options.color || '#111827',
            fontSize: options.size || '0.84rem',
            fontWeight: options.weight || 850,
            lineHeight: 1.35,
            fontFamily: options.monospace ? 'monospace' : 'inherit',
            overflowWrap: 'anywhere',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {displayValue}
        </Typography>
      </Box>
    );
  }

  renderReportCodes(values, emptyLabel = "None") {
    const codes = Array.isArray(values) ? values : [];

    if (codes.length === 0) {
      return (
        <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 650 }}>
          {emptyLabel}
        </Typography>
      );
    }

    return (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {codes.map((code, index) => (
          <Chip
            key={`${String(code)}-${index}`}
            size="small"
            label={this.formatCode(code)}
            variant="outlined"
            sx={{
              height: 'auto',
              minHeight: 24,
              color: '#334155',
              bgcolor: '#f8fafc',
              borderColor: 'rgba(15, 23, 42, 0.12)',
              fontSize: '0.68rem',
              fontWeight: 750,
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                py: 0.35,
              },
            }}
          />
        ))}
      </Stack>
    );
  }

  renderReportAccordion(title, subtitle, children, defaultExpanded = false) {
    return (
      <Accordion
        defaultExpanded={defaultExpanded}
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid rgba(15, 23, 42, 0.08)',
          borderRadius: '12px !important',
          overflow: 'hidden',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: { xs: 1.1, sm: 1.5 },
            minHeight: 54,
            bgcolor: '#fff',
            '& .MuiAccordionSummary-content': { my: 0.9 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#334155', fontSize: '0.84rem', fontWeight: 900 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography sx={{ mt: 0.2, color: '#64748b', fontSize: '0.7rem', fontWeight: 650 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            px: { xs: 1.1, sm: 1.5 },
            pt: 1.25,
            pb: 1.5,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#f8fafc',
          }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    );
  }

  getSalesDownOutcomeCopy(result, isPositive = false) {
    const percentage = Math.abs(Number(result.percentage));
    const percentageText = Number.isFinite(percentage) ? `${percentage.toFixed(1)}%` : null;

    switch (result.outcome) {
      case 'anomaly':
        return {
          title: percentageText
            ? `Sales were ${percentageText} ${isPositive ? 'higher' : 'lower'} than expected`
            : `Sales were ${isPositive ? 'higher' : 'lower'} than expected`,
          description: `The ${isPositive ? 'increase' : 'decrease'} passed all three limits required to create a sales alert.`,
          color: isPositive ? '#166534' : '#991b1b',
          background: isPositive ? 'rgba(34, 197, 94, 0.07)' : 'rgba(239, 68, 68, 0.07)',
        };
      case 'normal':
        return {
          title: 'Sales stayed within the alert limits',
          description: 'The comparison was completed, but the decrease was not large enough to create an alert.',
          color: '#166534',
          background: 'rgba(34, 197, 94, 0.07)',
        };
      case 'suppressed':
        return {
          title: 'No alert was created',
          description: 'The calculation was completed, but a minimum business limit was not reached.',
          color: '#92400e',
          background: 'rgba(245, 158, 11, 0.08)',
        };
      case 'insufficient_history':
        return {
          title: 'There is not enough sales history',
          description: 'A reliable expected value could not be calculated for this date.',
          color: '#92400e',
          background: 'rgba(245, 158, 11, 0.08)',
        };
      case 'data_quality':
        return {
          title: 'The available sales data needs attention',
          description: 'The report could not make a reliable comparison because required data was missing or invalid.',
          color: '#991b1b',
          background: 'rgba(239, 68, 68, 0.07)',
        };
      default:
        return {
          title: 'Sales comparison result',
          description: 'Review the calculation summary below.',
          color: '#334155',
          background: '#f8fafc',
        };
    }
  }

  formatFactorChange(value) {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const factor = Number(value);
    if (!Number.isFinite(factor)) {
      return '-';
    }

    const percentage = (factor - 1) * 100;
    if (Math.abs(percentage) < 0.05) {
      return 'No material change';
    }

    return `${Math.abs(percentage).toFixed(1)}% ${percentage > 0 ? 'higher' : 'lower'}`;
  }

  renderThresholdCheck(label, passed, detail) {
    const evaluated = passed === true || passed === false;
    const positive = passed === true;
    const color = !evaluated ? '#64748b' : positive ? '#166534' : '#92400e';
    const background = !evaluated
      ? '#f8fafc'
      : positive
        ? 'rgba(34, 197, 94, 0.07)'
        : 'rgba(245, 158, 11, 0.08)';

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          borderRadius: '10px',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: background,
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={0.7}>
          {positive ? (
            <CheckCircleOutlineIcon sx={{ mt: 0.05, color, fontSize: '1rem', flexShrink: 0 }} />
          ) : (
            <BlockOutlinedIcon sx={{ mt: 0.05, color, fontSize: '1rem', flexShrink: 0 }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color, fontSize: '0.76rem', fontWeight: 900 }}>
              {label}: {!evaluated ? 'Not evaluated' : positive ? 'Passed' : 'Not passed'}
            </Typography>
            <Typography sx={{ mt: 0.2, color: '#64748b', fontSize: '0.7rem', fontWeight: 650, lineHeight: 1.4 }}>
              {detail}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  }

  cleanText(value) {
    if (!value) {
      return "";
    }

    return String(value).replace(/(-?\d+\.\d{2,})(%?)/g, (match, number, percent) => {
      const parsed = Number(number);

      if (!Number.isFinite(parsed)) {
        return match;
      }

      if (percent) {
        return `${parsed.toFixed(1)}%`;
      }

      if (Math.abs(parsed) >= 100) {
        return parsed.toLocaleString("ro-RO", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      }

      return parsed.toFixed(1);
    });
  }

  renderInsightCard(row) {
    const direction = this.getDirection(row);
    const severitySx = this.getSeverityColor(row.severity);
    const statusSx = this.getStatusColor(row.status);
    const diffValue = Number(row.difference_percent || 0);
    const isNegative = diffValue < 0;
    const isSummary = this.isDailySummary(row);
    const locationLabel = this.getLocationLabel(row);

    const title = row.title || 'Insight';

    return (
      <Paper
        key={row.iref}
        variant="outlined"
        onClick={() => this.openInsight(row)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            this.openInsight(row);
          }
        }}
        sx={{
          flexShrink: 0,
          borderRadius: { xs: '12px', md: '14px' },
          overflow: 'hidden',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
          boxShadow: '0 2px 10px rgba(15, 23, 42, 0.045)',
          cursor: 'pointer',
          transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
          '&:hover': {
            borderColor: 'rgba(26, 35, 126, 0.22)',
            boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            minHeight: 112,
            gap: 0,

            '@media (min-width: 1020px)': {
              gridTemplateColumns: 'minmax(280px, 360px) minmax(110px, 1fr) minmax(110px, 1fr) minmax(110px, 1fr)',
              minHeight: 104,
            },
          }}
        >
          <Box
            sx={{
              p: 0.85,
              '@media (min-width: 1020px)': {
                p: 1.25,
              },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                mb: { xs: 0.6, sm: 0.35 },
                flexWrap: 'wrap',
                gap: 0.35,
              }}
            >
              <Chip
                label={direction.label}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 850,
                  color: direction.color,
                  bgcolor: direction.bg,
                  border: direction.border,
                }}
              />

              {isSummary ? (
                <Chip
                  label={this.getInsightTypeLabel(row)}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                    color: '#1a237e',
                    bgcolor: 'rgba(26, 35, 126, 0.06)',
                    border: '1px solid rgba(26, 35, 126, 0.14)',
                  }}
                />
              ) : null}

              <Chip
                label={this.getSeverityLabel(row)}
                size="small"
                variant="outlined"
                sx={{
                  ...severitySx,
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 850,
                }}
              />

              <Chip
                label={this.getStatusLabel(row)}
                size="small"
                variant="outlined"
                sx={{
                  ...statusSx,
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 850,
                }}
              />

              {row.review_note ? (
                <Chip
                  icon={<NotesOutlinedIcon sx={{ fontSize: '0.9rem !important' }} />}
                  label="Note"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                    color: '#334155',
                    bgcolor: 'rgba(100, 116, 139, 0.08)',
                    border: '1px solid rgba(100, 116, 139, 0.16)',
                    '& .MuiChip-icon': {
                      color: '#64748b',
                      ml: 0.5,
                    },
                  }}
                />
              ) : null}
            </Stack>

            <Tooltip title={title} arrow placement="top-start">
              <Typography
                sx={{
                  color: '#111827',
                  fontWeight: 900,
                  fontSize: '0.9rem',
                  WebkitLineClamp: 2,
                  '@media (min-width: 1020px)': {
                    fontSize: '1rem',
                  },
                  lineHeight: 1.2,
                  display: '-webkit-box',
                  textAlign: 'left',
                  WebkitLineClamp: { xs: 2, md: 2 },
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {title}
              </Typography>
            </Tooltip>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                mt: { xs: 0.65, sm: 0.45 },
                color: '#64748b',
                fontSize: '0.72rem',
                fontWeight: 700,
                minWidth: 0,
                flexWrap: 'wrap',
              }}
            >
              <Tooltip title={locationLabel} arrow>
                <Typography
                  sx={{
                    color: '#334155',
                    fontSize: '0.74rem',
                    fontWeight: 800,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {locationLabel}
                </Typography>
              </Tooltip>

              <Typography sx={{ color: '#cbd5e1' }}>•</Typography>

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.72rem',
                  fontWeight: 750,
                  whiteSpace: 'nowrap',
                }}
              >
                {this.isTransactionTrend(row) && row.trend
                  ? `${row.trend.closed_week ? 'Ended' : 'Ongoing'} · as of ${row.trend.week_end || 'unknown'}`
                  : this.formatDate(row.period_start)}
              </Typography>
              {this.renderTrendLink(row)}
            </Stack>
          </Box>

          <Box
            sx={{
              p: { xs: 0.85 },
              bgcolor: '#f8fafc',
              borderTop: '1px solid rgba(15, 23, 42, 0.06)',
              display: 'grid',
              gridTemplateColumns: '1.05fr 1fr 1fr',
              columnGap: 1.25,
              alignItems: 'center',

              '@media (min-width: 1020px)': {
                p: 0,
                bgcolor: 'transparent',
                borderTop: 0,
                display: 'contents',

                '& > div': {
                  px: 1.5,
                  py: 0,
                  minHeight: 104,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  borderLeft: '1px solid rgba(15, 23, 42, 0.06)',
                  bgcolor: isSummary
                    ? 'rgba(26, 35, 126, 0.025)'
                    : isNegative
                      ? 'rgba(239, 68, 68, 0.028)'
                      : 'rgba(34, 197, 94, 0.028)',
                },
              },
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                '@media (min-width: 1020px)': {
                  alignItems: 'center',
                }
              }}
            >
              <Typography
                sx={{
                  color: isNegative ? '#991b1b' : '#166534',
                  fontWeight: 950,
                  fontSize: { xs: '1.05rem', md: '1.35rem' },
                  lineHeight: 1,
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {this.formatPercent(row.difference_percent)}
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  color: '#64748b',
                  fontWeight: 850,
                  fontSize: { xs: '0.62rem', md: '0.68rem' },
                  lineHeight: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.25,
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                  whiteSpace: 'nowrap',
                }}
              >
                {this.isTransactionTrend(row) ? 'Vs last year · tx' : 'Vs expected'}
              </Typography>
            </Box>

            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                '@media (min-width: 1020px)': {
                  alignItems: 'center',
                }
              }}
            >
              <Typography
                sx={{
                  color: '#64748b',
                  fontWeight: 800,
                  fontSize: { xs: '0.62rem', sm: '0.62rem' },
                  textTransform: 'uppercase',
                  letterSpacing: 0.25,
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                }}
              >
                {this.isTransactionTrend(row) ? 'Transactions' : 'Actual'}
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#111827',
                  fontWeight: 900,
                  fontSize: { xs: '0.86rem', md: '1.05rem' },
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  alignItems: 'flex-start',
                  '@media (min-width: 1020px)': {
                    alignItems: 'center',
                  }
                }}
              >
                {this.formatMoney(row.actual_value)}
              </Typography>
            </Box>

            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                '@media (min-width: 1020px)': {
                  alignItems: 'center',
                }
              }}
            >
              <Typography
                sx={{
                  color: '#64748b',
                  fontWeight: 800,
                  fontSize: { xs: '0.62rem', sm: '0.62rem' },
                  textTransform: 'uppercase',
                  letterSpacing: 0.25,
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                }}
              >
                {this.isTransactionTrend(row) ? 'Last year' : 'Expected'}
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#111827',
                  fontWeight: 900,
                  fontSize: { xs: '0.86rem', md: '1.05rem' },
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  '@media (min-width: 1020px)': {
                    textAlign: 'center',
                  },
                }}
              >
                {this.formatMoney(row.expected_value)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  }

  renderInsightDrawer() {
    const row = this.state.selectedInsight;

    if (!row) {
      return null;
    }

    const direction = this.getDirection(row);
    const severitySx = this.getSeverityColor(row.severity);
    const statusSx = this.getStatusColor(row.status);
    const status = Number(row.status);
    const isSummary = this.isDailySummary(row);
    const locationLabel = this.getLocationLabel(row);

    const actionButtonSx = {
      justifyContent: 'flex-start',
      borderRadius: 1.5,
      textTransform: 'none',
      fontWeight: 850,
      fontSize: '0.82rem',
      py: 0.85,
    };

    return (
      <Drawer
        anchor="right"
        open={this.state.drawerOpen}
        onClose={this.closeInsightDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
            bgcolor: '#f8fafc',
          }
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              bgcolor: '#fff',
              borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 950,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                }}
              >
                Insight review
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#64748b',
                  fontWeight: 650,
                  fontSize: '0.76rem',
                }}
              >
                {locationLabel} • {this.formatDate(row.period_start)}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={this.closeInsightDrawer}
              sx={{
                color: '#64748b',
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'auto',
              p: 1.25,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Stack
                direction="row"
                spacing={0.35}
                alignItems="center"
                sx={{
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={direction.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                    color: direction.color,
                    bgcolor: direction.bg,
                    border: direction.border,
                  }}
                />

                <Chip
                  label={this.getSeverityLabel(row)}
                  size="small"
                  variant="outlined"
                  sx={{
                    ...severitySx,
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                  }}
                />

                <Chip
                  label={this.getStatusLabel(row)}
                  size="small"
                  variant="outlined"
                  sx={{
                    ...statusSx,
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                  }}
                />
              </Stack>

              <Typography
                sx={{
                  color: '#111827',
                  fontWeight: 950,
                  fontSize: '1rem',
                  lineHeight: 1.25,
                }}
              >
                {row.title || 'Insight'}
              </Typography>

              {row.summary ? (
                <Typography
                  sx={{
                    mt: 0.7,
                    color: '#64748b',
                    fontWeight: 500,
                    fontSize: '0.82rem',
                    lineHeight: 1.45,
                  }}
                >
                  {this.cleanText(row.summary)}
                </Typography>
              ) : null}

              {this.renderTrendLink(row)}
              {this.isTransactionTrend(row) && row.trend ? (
                <Typography variant="caption" display="block">Opened {row.trend.opened_week}; last assessed {row.trend.week_end}. {row.trend.closed_week ? `Ended ${row.trend.closed_week}.` : 'Episode ongoing; review status is separate.'}</Typography>
              ) : null}
              {(
                Number(row.insight_type) === INSIGHT_TYPE_SALES_VALUE_BELOW_EXPECTED
                || Number(row.insight_type) === INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED
              ) ? (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AssessmentOutlinedIcon />}
                  onClick={this.openSalesExplanation}
                  sx={{
                    mt: 1.1,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 900,
                    justifyContent: 'flex-start',
                  }}
                >
                  View sales explanation
                </Button>
              ) : null}
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Typography
                sx={{
                  color: '#334155',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  mb: 1,
                }}
              >
                Numbers
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 0.75,
                }}
              >
                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    {this.isTransactionTrend(row) ? 'Actual transactions' : 'Actual'}
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.95rem', fontWeight: 950 }}>
                    {this.isTransactionTrend(row) ? this.formatMoney(row.actual_value) : this.formatMoneyDetailed(row.actual_value)}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    {this.isTransactionTrend(row) ? 'Reference transactions' : 'Expected'}
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.95rem', fontWeight: 950 }}>
                    {this.isTransactionTrend(row) ? this.formatMoney(row.expected_value) : this.formatMoneyDetailed(row.expected_value)}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    Difference
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.95rem', fontWeight: 950 }}>
                    {this.isTransactionTrend(row) ? this.formatMoney(row.difference_value) : this.formatMoneyDetailed(row.difference_value)}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    Difference %
                  </Typography>
                  <Typography
                    sx={{
                      color: Number(row.difference_percent || 0) < 0 ? '#991b1b' : '#166534',
                      fontSize: '0.95rem',
                      fontWeight: 950,
                    }}
                  >
                    {this.formatPercent(row.difference_percent)}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Typography
                sx={{
                  color: '#334155',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  mb: 1,
                }}
              >
                Context
              </Typography>

              <Stack spacing={0.65}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Type
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {this.getInsightTypeLabel(row)}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Location
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {locationLabel}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Period
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {this.formatDate(row.period_start)} - {this.formatDate(row.period_end)}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Baseline
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {row.baseline_type || '-'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                    Confidence
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                    {row.confidence !== null && row.confidence !== undefined ? row.confidence : '-'}
                  </Typography>
                </Stack>

                {isSummary && row.source_data ? (
                  <React.Fragment>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                        Locations included
                      </Typography>
                      <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                        {row.source_data.locations_included !== undefined ? row.source_data.locations_included : '-'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                        Locations skipped
                      </Typography>
                      <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 850, textAlign: 'right' }}>
                        {row.source_data.locations_skipped !== undefined ? row.source_data.locations_skipped : '-'}
                      </Typography>
                    </Stack>
                  </React.Fragment>
                ) : null}
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Typography
                sx={{
                  color: '#334155',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  mb: 1,
                }}
              >
                Review note
              </Typography>

              <TextField
                value={this.state.reviewNote}
                onChange={this.handleReviewNoteChange}
                fullWidth
                multiline
                minRows={3}
                size="small"
                placeholder="Example: Location closed, relocation, stock issue, known event, data issue..."
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '1rem',
                    borderRadius: 1.5,
                    bgcolor: '#fff',
                  },
                }}
              />
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#fff',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Typography
                sx={{
                  color: '#334155',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  mb: 1,
                }}
              >
                Review actions
              </Typography>

              <Stack spacing={0.65}>
                {status === 0 ? (
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityOutlinedIcon />}
                    onClick={() => this.setInsightStatus(1)}
                    sx={actionButtonSx}
                  >
                    Mark as Seen
                  </Button>
                ) : null}

                {[0, 1, 3, 4, 9].includes(status) ? (
                  <Button
                    variant="outlined"
                    startIcon={<RateReviewOutlinedIcon />}
                    onClick={() => this.setInsightStatus(2)}
                    sx={actionButtonSx}
                  >
                    Move to Review
                  </Button>
                ) : null}

                {[0, 1, 2].includes(status) ? (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => this.setInsightStatus(3)}
                    sx={actionButtonSx}
                  >
                    Resolve
                  </Button>
                ) : null}

                {[0, 1, 2].includes(status) ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<BlockOutlinedIcon />}
                    onClick={() => this.setInsightStatus(4)}
                    sx={actionButtonSx}
                  >
                    Ignore
                  </Button>
                ) : null}

                {[2, 3, 4].includes(status) ? (
                  <Button
                    variant="outlined"
                    startIcon={<ArchiveOutlinedIcon />}
                    onClick={() => this.setInsightStatus(9)}
                    sx={actionButtonSx}
                  >
                    Archive
                  </Button>
                ) : null}
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Drawer>
    );
  }

  renderSixWeekDevelopment(context, isPositive) {
    const weeks = context && context.rolling_weeks;
    if (!weeks) {
      return this.renderReportAccordion(
        'Six-week development',
        'Not available in this stored calculation',
        <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>
          Regenerate this insight to include its six-week comparison.
        </Typography>
      );
    }
    const direction = isPositive ? 'higher' : 'lower';
    const comparable = weeks.weeks_comparable;
    const rows = [...(weeks.weeks || []), ...(weeks.skipped || [])]
      .sort((a, b) => a.week_end.localeCompare(b.week_end));
    return this.renderReportAccordion(
      'Six-week development',
      `${comparable}/${weeks.weeks_requested} comparable weeks · through ${this.formatDate(weeks.period_end)}`,
      <Stack spacing={1.2}>
        <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
          {this.formatDate(weeks.period_start)} – {this.formatDate(weeks.period_end)}. Six rolling seven-day periods compared with matching weekdays last year. This context does not affect the daily alert.
        </Typography>
        {comparable > 0 ? (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
              {this.renderReportValue(`Sales ${direction}`, `${weeks[`sales_${direction}_weeks`]}/${comparable} weeks`)}
              {this.renderReportValue(`Transactions ${direction}`, `${weeks[`transactions_${direction}_weeks`]}/${comparable} weeks`)}
              {this.renderReportValue(`Transactions ${isPositive ? 'lower' : 'higher'}`, `${weeks[`transactions_${isPositive ? 'lower' : 'higher'}_weeks`]}/${comparable} weeks`)}
              {this.renderReportValue('Longest transaction run', `${weeks[`transactions_longest_${direction}_run_weeks`]} weeks`, {
                help: `Consecutive weeks with ${direction} transactions. Excluded weeks break the run.`,
              })}
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
              Transactions in comparable weeks: {this.formatMoney(weeks.current_transactions)} vs {this.formatMoney(weeks.reference_transactions)} last year.
            </Typography>
          </>
        ) : (
          <Alert severity="info">No complete comparable weeks are available. This is insufficient evidence, not a zero-sales result.</Alert>
        )}
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" aria-label="Six-week sales and transaction comparison" sx={{ width: '100%', minWidth: 380, borderCollapse: 'collapse', fontSize: '0.73rem', '& th, & td': { textAlign: 'left', p: 0.8, borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' } }}>
            <thead><tr><th>Week ending</th><th>Sales</th><th>Transactions</th></tr></thead>
            <tbody>
              {rows.map((week) => (
                <tr key={week.week_end}>
                  <td>
                    {this.formatDate(week.week_end)}
                    {week.reference_week_end ? (
                      <Box component="span" sx={{ display: 'block', fontSize: '0.66rem', color: '#64748b' }}>
                        Reference: {this.formatDate(week.reference_week_start)} – {this.formatDate(week.reference_week_end)}
                      </Box>
                    ) : null}
                  </td>
                  {week.reason ? (
                    <td colSpan={2}>Excluded: {this.formatCode(week.reason)}</td>
                  ) : (
                    <>
                      <td>
                        {this.formatPercent(week.sales_percent)}
                        <Box component="span" sx={{ display: 'block', color: '#64748b' }}>{this.formatMoney(week.current_sales)} vs {this.formatMoney(week.reference_sales)} RON</Box>
                      </td>
                      <td>
                        {this.formatPercent(week.transactions_percent)}
                        <Box component="span" sx={{ display: 'block', color: '#64748b' }}>{this.formatMoney(week.current_transactions)} vs {this.formatMoney(week.reference_transactions)}</Box>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </Stack>
    );
  }

  renderSalesDownReportModal() {
    const row = this.state.selectedInsight;
    const report = this.state.salesDownReport;
    const isPositive = Number(row && row.insight_type) === INSIGHT_TYPE_SALES_VALUE_ABOVE_EXPECTED;
    const isSalesValueReport = report && report.schema_version === SALES_VALUE_REPORT_SCHEMA_VERSION;
    const changeLabel = isPositive ? 'increase' : 'decrease';
    const result = report && report.result ? report.result : {};
    const confidence = result.confidence || {};
    const selectedComparator = result.selected_comparator || {};
    const trend = result.trend || {};
    const monthly = result.monthly || {};
    const sourceFinality = result.source_finality || {};
    const targetEvidence = result.target_evidence || {};
    const currencyEvidence = result.currency_evidence || {};
    const locationEligibility = result.location_eligibility || {};
    const threshold = result.threshold_evidence || {};
    const driverAnalysis = report && report.driver_analysis
      ? report.driver_analysis
      : {};
    const driverActual = driverAnalysis.actual || {};
    const driverReference = driverAnalysis.reference || {};
    const driverChanges = driverAnalysis.changes || {};
    const transactionChange = driverChanges.transactions || {};
    const averageSaleChange = driverChanges.average_sale_per_transaction || {};
    const driverContributions = driverAnalysis.estimated_contributions || {};
    const productAnalysis = report && report.product_driver_analysis
      ? report.product_driver_analysis
      : {};
    const productActual = productAnalysis.actual || {};
    const productReference = productAnalysis.reference || {};
    const productChanges = productAnalysis.changes || {};
    const productUnitChange = productChanges.units || {};
    const productAverageChange = productChanges.average_realized_price || {};
    const productContributions = productAnalysis.estimated_contributions || {};
    const productEvidence = productAnalysis.evidence || {};
    const productReconciliation = productAnalysis.reconciliation || {};
    const productActualReconciliation = productReconciliation.actual || {};
    const productReferenceReconciliation = productReconciliation.reference || {};
    const rankedProducts = isPositive ? productAnalysis.top_product_gains : productAnalysis.top_product_declines;
    const topProductChanges = Array.isArray(rankedProducts)
      ? rankedProducts.slice(0, 3)
      : [];
    const comparatorDecisions = Array.isArray(result.comparator_decisions)
      ? result.comparator_decisions
      : [];
    const sources = Array.isArray(sourceFinality.sources)
      ? sourceFinality.sources
      : [];
    const reportCurrency = result.currency || currencyEvidence.declared_currency || "RON";
    const isNegative = Number(result.percentage) < 0;
    const showLoading = this.state.salesDownReportLoading && !this.state.salesDownReportError;
    const outcomeCopy = this.getSalesDownOutcomeCopy(result, isPositive);
    const expectedSources = Array.isArray(sourceFinality.expected_source_refs)
      ? sourceFinality.expected_source_refs
      : [];
    const finalizedSources = Array.isArray(sourceFinality.finalized_source_refs)
      ? sourceFinality.finalized_source_refs
      : [];
    const missingSources = Array.isArray(sourceFinality.missing_source_refs)
      ? sourceFinality.missing_source_refs
      : [];
    const integrityIssues = Array.isArray(result.integrity_issues)
      ? result.integrity_issues
      : [];
    const hasNumericEvidence = [result.actual, result.expected, result.difference, result.percentage]
      .every((value) => value !== null && value !== undefined && value !== '');
    const isAnomaly = result.outcome === 'anomaly';
    const decisionHeading = isAnomaly ? 'Why you received this alert' : 'Why this result was produced';
    const severityLabel = result.severity === 'high'
      ? 'High attention'
      : result.severity === 'medium'
        ? 'Medium attention'
        : 'No alert severity';
    const confidenceNumber = confidence.value == null ? NaN : Number(confidence.value);
    const confidenceText = Number.isFinite(confidenceNumber)
      ? `${confidenceNumber.toFixed(0)}%`
      : '-';
    const driverAvailable = (
      driverAnalysis.schema_version === SALES_DRIVER_ANALYSIS_SCHEMA_VERSION &&
      driverAnalysis.status === 'available'
    );
    const driverHeadline = Number(transactionChange.percentage) < 0 && Number(averageSaleChange.percentage) > 0
      ? 'Higher average ticket offsets fewer transactions'
      : Number(transactionChange.percentage) < 0 && Number(averageSaleChange.percentage) < 0
        ? 'Transactions and average ticket are both declining'
      : driverAnalysis.main_observed_driver === 'transactions'
      ? `${Number(transactionChange.percentage) > 0 ? 'More' : 'Fewer'} transactions were the main observed driver`
      : driverAnalysis.main_observed_driver === 'average_sale_per_transaction'
        ? `A ${Number(averageSaleChange.percentage) > 0 ? 'higher' : 'lower'} average sale per transaction was the main observed driver`
        : driverAnalysis.main_observed_driver === 'both_or_unclear'
          ? 'No single dominant observed driver'
          : `Sales were not ${isPositive ? 'higher' : 'lower'} than the selected reference day`;
    const productAvailable = (
      productAnalysis.schema_version === PRODUCT_DRIVER_ANALYSIS_SCHEMA_VERSION &&
      productAnalysis.status === 'available'
    );
    const productHeadline = productAnalysis.main_observed_driver === 'volume'
      ? `${Number(productContributions.volume) > 0 ? 'More' : 'Fewer'} items sold were the main observed item driver`
      : productAnalysis.main_observed_driver === 'realized_price'
        ? `${Number(productContributions.realized_price) > 0 ? 'Higher' : 'Lower'} realized prices for comparable items were the main observed item driver`
        : productAnalysis.main_observed_driver === 'product_mix'
          ? 'Item mix was the main observed item driver'
          : `Item sales were not ${isPositive ? 'higher' : 'lower'} than the selected reference day`;
    const actualItemSource = productEvidence.actual_item_source || '';
    const referenceItemSource = productEvidence.reference_item_source || '';
    const itemSourcesDiffer = Boolean(
      actualItemSource &&
      referenceItemSource &&
      actualItemSource !== referenceItemSource
    );
    const itemSourceLabel = (source) => {
      if (source === 'order_items') {
        return 'Order items';
      }
      if (source === 'legacy_history_products') {
        return 'legacy product history';
      }
      return 'a different item source';
    };
    const volumeImpact = Number(productContributions.volume);
    const priceImpact = Number(productContributions.realized_price);
    const mixImpact = Number(productContributions.product_mix);
    const averagePerItemChange = Number(productAverageChange.percentage);
    const itemImpactSummary = [];
    if (priceImpact > 0 && averagePerItemChange < 0) {
      itemImpactSummary.push(
        'Prices on comparable items helped sales, even though the overall average sales per item fell.'
      );
    } else if (priceImpact < 0) {
      itemImpactSummary.push('Lower realized prices on comparable items reduced sales.');
    } else if (priceImpact > 0) {
      itemImpactSummary.push('Prices on comparable items helped sales.');
    }
    if (Number.isFinite(volumeImpact) && volumeImpact < 0) {
      itemImpactSummary.push('Selling fewer items also reduced sales.');
    } else if (Number.isFinite(volumeImpact) && volumeImpact > 0) {
      itemImpactSummary.push('Selling more items helped sales.');
    }
    if (Number.isFinite(mixImpact) && mixImpact < 0) {
      itemImpactSummary.push('The item mix had an additional negative impact.');
    } else if (Number.isFinite(mixImpact) && mixImpact > 0) {
      itemImpactSummary.push(isPositive ? 'The item mix helped sales.' : 'The item mix partially offset the decline.');
    }
    const salesDriverReferenceDate = driverAnalysis.reference_date ||
      productAnalysis.reference_date || selectedComparator.observation_date;
    const impactColor = (value) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || numericValue === 0) {
        return '#475569';
      }
      return numericValue < 0 ? '#991b1b' : '#166534';
    };

    return (
      <Dialog
        open={Boolean(this.state.salesDownReportOpen)}
        onClose={this.closeSalesDownReport}
        fullWidth
        maxWidth="md"
        aria-labelledby="sales-explanation-title"
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            width: { xs: '100%', sm: 'calc(100% - 32px)' },
            maxHeight: { xs: '100%', sm: 'calc(100% - 32px)' },
            height: { xs: '100%', sm: 'auto' },
            borderRadius: { xs: 0, sm: '16px' },
            bgcolor: '#f8fafc',
          }
        }}
      >
        <DialogTitle
          id="sales-explanation-title"
          sx={{
            px: { xs: 1.25, sm: 2 },
            py: 1.25,
            bgcolor: '#fff',
            borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#1a237e', fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 950 }}>
                Sales performance explanation
              </Typography>
              <Typography sx={{ mt: 0.25, color: '#64748b', fontSize: '0.75rem', fontWeight: 650 }}>
                {row ? this.getLocationLabel(row) : '-'} • {this.formatDate(result.business_date || (row && row.period_start))}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label="Close sales explanation"
              onClick={this.closeSalesDownReport}
              sx={{ color: '#64748b', flexShrink: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 1, sm: 1.5 }, bgcolor: '#f8fafc' }}>
          {showLoading ? (
            <Stack role="status" alignItems="center" justifyContent="center" spacing={1.25} sx={{ minHeight: 260 }}>
              <CircularProgress size={34} />
              <Typography sx={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 750 }}>
                Loading the stored report…
              </Typography>
            </Stack>
          ) : null}

          {this.state.salesDownReportError ? (
            <Alert severity="error" sx={{ my: 1, '& .MuiAlert-message': { fontSize: '0.82rem', fontWeight: 700 } }}>
              {this.state.salesDownReportError}
            </Alert>
          ) : null}

          {!showLoading && !this.state.salesDownReportError && report ? (
            <Stack spacing={1}>
              {report.snapshot_state === 'historical' ? <Alert severity="warning">Historical snapshot: this alert was automatically archived ({this.formatCode(report.snapshot_reason)}). These figures describe its earlier calculation, not a current qualifying alert.</Alert> : null}
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.25, sm: 1.6 },
                  borderRadius: '14px',
                  borderColor: 'rgba(15, 23, 42, 0.08)',
                  bgcolor: outcomeCopy.background,
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: outcomeCopy.color, fontSize: { xs: '1rem', sm: '1.15rem' }, fontWeight: 950, lineHeight: 1.3 }}>
                      {outcomeCopy.title}
                    </Typography>
                    <Typography sx={{ mt: 0.35, color: '#475569', fontSize: '0.76rem', fontWeight: 650, lineHeight: 1.45 }}>
                      {outcomeCopy.description}
                    </Typography>
                  </Box>
                  <Chip
                    label={severityLabel}
                    size="small"
                    variant="outlined"
                    sx={{ color: outcomeCopy.color, borderColor: outcomeCopy.color, fontWeight: 900, flexShrink: 0 }}
                  />
                </Stack>

                {hasNumericEvidence ? (
                  <>
                    <Divider sx={{ my: 1.25 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 1, sm: 1.5 } }}>
                      {this.renderReportValue('Actual sales', `${this.formatMoneyDetailed(result.actual)} ${reportCurrency}`)}
                      {this.renderReportValue('Expected sales', `${this.formatMoneyDetailed(result.expected)} ${reportCurrency}`, {
                        help: 'Estimated from a comparable day, adjusted by the recent sales trend and, when available, monthly context.',
                      })}
                      {this.renderReportValue('Value difference', `${this.formatMoneyDetailed(result.difference)} ${reportCurrency}`, { color: isNegative ? '#991b1b' : '#166534' })}
                      {this.renderReportValue('Percentage difference', this.formatPercent(result.percentage), { color: isNegative ? '#991b1b' : '#166534' })}
                    </Box>
                  </>
                ) : null}
              </Paper>

              <Paper variant="outlined" sx={{ p: { xs: 1.1, sm: 1.5 }, borderRadius: '14px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                <Typography sx={{ color: '#334155', fontSize: '0.84rem', fontWeight: 900 }}>
                  Sales drivers
                </Typography>
                {salesDriverReferenceDate ? (
                  <Typography sx={{ mt: 0.2, color: '#64748b', fontSize: '0.7rem', fontWeight: 650 }}>
                    Compared with the selected reference day, {this.formatDate(salesDriverReferenceDate)}.
                  </Typography>
                ) : null}
                {driverAvailable ? (
                  <Typography sx={{ mt: 0.2, color: '#64748b', fontSize: '0.7rem' }}>
                    Raw sales change: {this.formatPercent((driverChanges.sales || {}).percentage)} · before trend and monthly adjustments.
                  </Typography>
                ) : null}

                <Stack spacing={0.65} sx={{ mt: 0.9 }}>
                  {driverAvailable ? (
                    <Paper variant="outlined" sx={{ px: 1, py: 0.8, borderRadius: '10px', borderColor: 'rgba(26, 35, 126, 0.16)', bgcolor: 'rgba(26, 35, 126, 0.04)' }}>
                      <Typography sx={{ color: '#64748b', fontSize: '0.66rem', fontWeight: 850 }}>Customer activity</Typography>
                      <Typography sx={{ mt: 0.15, color: '#1a237e', fontSize: '0.8rem', fontWeight: 950, lineHeight: 1.35 }}>
                        {driverHeadline}
                      </Typography>
                    </Paper>
                  ) : null}
                  {productAvailable ? (
                    <Paper variant="outlined" sx={{ px: 1, py: 0.8, borderRadius: '10px', borderColor: 'rgba(26, 35, 126, 0.16)', bgcolor: 'rgba(26, 35, 126, 0.04)' }}>
                      <Typography sx={{ color: '#64748b', fontSize: '0.66rem', fontWeight: 850 }}>Item sales</Typography>
                      <Typography sx={{ mt: 0.15, color: '#1a237e', fontSize: '0.8rem', fontWeight: 950, lineHeight: 1.35 }}>
                        {productHeadline}
                      </Typography>
                    </Paper>
                  ) : null}
                </Stack>

                {driverAvailable || productAvailable ? (
                  <Typography sx={{ mt: 0.75, color: '#64748b', fontSize: '0.68rem', fontWeight: 650, lineHeight: 1.4 }}>
                    These are observed contributions to the sales change. They do not prove why customer behaviour changed.
                  </Typography>
                ) : null}

                <Stack spacing={0.7} sx={{ mt: 1 }}>
                  {this.renderReportAccordion(
                    'Customer activity details',
                    'Transactions and average sale per transaction',
                    driverAvailable ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
                        <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                          <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>Transactions</Typography>
                          <Typography sx={{ mt: 0.3, color: '#111827', fontSize: '0.9rem', fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
                            {this.formatMoney(driverActual.transactions)}
                          </Typography>
                          <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.7rem', fontWeight: 650 }}>
                            Reference: {this.formatMoney(driverReference.transactions)} • Change: {this.formatPercent(transactionChange.percentage)}
                          </Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                          <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>Average sale per transaction</Typography>
                          <Typography sx={{ mt: 0.3, color: '#111827', fontSize: '0.9rem', fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
                            {this.formatMoneyDetailed(driverActual.average_sale_per_transaction)} {reportCurrency}
                          </Typography>
                          <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.7rem', fontWeight: 650 }}>
                            Reference: {this.formatMoneyDetailed(driverReference.average_sale_per_transaction)} {reportCurrency} • Change: {this.formatPercent(averageSaleChange.percentage)}
                          </Typography>
                        </Paper>
                        {this.renderReportValue('Transaction contribution', `${this.formatMoneyDetailed(driverContributions.transactions)} ${reportCurrency}`, { color: impactColor(driverContributions.transactions) })}
                        {this.renderReportValue('Average-ticket contribution', `${this.formatMoneyDetailed(driverContributions.average_sale_per_transaction)} ${reportCurrency}`, { color: impactColor(driverContributions.average_sale_per_transaction) })}
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.74rem', fontWeight: 650 } }}>
                        Sales driver analysis is not available for this stored calculation. The sales alert and its evidence remain valid.
                      </Alert>
                    )
                  )}

                  {!isPositive || report.product_driver_analysis ? this.renderReportAccordion(
                    'Item sales details',
                    'Items sold, average sales per item and sales impacts',
                    productAvailable ? (
                      <>
                        <Typography sx={{ mb: 0.75, color: '#64748b', fontSize: '0.68rem', fontWeight: 650 }}>
                          The first two measures describe what was sold. The impact breakdown separately estimates how item volume, comparable-item prices and item mix contributed to the sales change.
                        </Typography>
                        <Typography sx={{ mb: 0.45, color: '#334155', fontSize: '0.7rem', fontWeight: 900 }}>
                          Item performance
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
                          <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                            <Stack direction="row" alignItems="center" spacing={0.35}>
                              <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>Items sold</Typography>
                              <Tooltip title="The number of sellable items. A combo counts as one item, and its component products are not counted again when Order evidence is available." arrow>
                                <InfoOutlinedIcon aria-label="Items sold information" sx={{ color: '#94a3b8', fontSize: '0.85rem', cursor: 'help' }} />
                              </Tooltip>
                            </Stack>
                            <Typography sx={{ mt: 0.3, color: '#111827', fontSize: '0.9rem', fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
                              {this.formatMoney(productActual.units)}
                            </Typography>
                            <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.7rem', fontWeight: 650 }}>
                              Reference: {this.formatMoney(productReference.units)} • Change: {this.formatPercent(productUnitChange.percentage)}
                            </Typography>
                          </Paper>
                          <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                            <Stack direction="row" alignItems="center" spacing={0.35}>
                              <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>Average sales per item</Typography>
                              <Tooltip title="Total sales divided by items sold. This overall average can decrease even when prices rise on items sold on both days, because the item mix can also change." arrow>
                                <InfoOutlinedIcon aria-label="Average sales per item information" sx={{ color: '#94a3b8', fontSize: '0.85rem', cursor: 'help' }} />
                              </Tooltip>
                            </Stack>
                            <Typography sx={{ mt: 0.3, color: '#111827', fontSize: '0.9rem', fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
                              {this.formatMoneyDetailed(productActual.average_realized_price)} {reportCurrency}
                            </Typography>
                            <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.7rem', fontWeight: 650 }}>
                              Reference: {this.formatMoneyDetailed(productReference.average_realized_price)} {reportCurrency} • Change: {this.formatPercent(productAverageChange.percentage)}
                            </Typography>
                          </Paper>
                        </Box>

                        {itemSourcesDiffer ? (
                          <Alert severity="warning" sx={{ mt: 0.9, '& .MuiAlert-message': { fontSize: '0.7rem', fontWeight: 650, lineHeight: 1.45 } }}>
                            Estimated comparison: the current day uses {itemSourceLabel(actualItemSource)}, while the reference uses {itemSourceLabel(referenceItemSource)}. Historical combo composition is not fully available, so the price and mix separation is approximate.
                          </Alert>
                        ) : null}

                        <Typography sx={{ mt: 1, mb: 0.45, color: '#334155', fontSize: '0.7rem', fontWeight: 900 }}>
                          Estimated sales impact
                        </Typography>
                        <Typography sx={{ mb: 0.6, color: '#64748b', fontSize: '0.67rem', fontWeight: 650, lineHeight: 1.4 }}>
                          Read these three effects together. They add up to the sales change versus the selected reference day.
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 0.75 }}>
                          <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                            <Stack direction="row" alignItems="center" spacing={0.35}>
                              <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>Items sold effect</Typography>
                              <Tooltip title="The estimated sales impact from selling more or fewer items, using the reference average sales per item." arrow>
                                <InfoOutlinedIcon aria-label="Items sold effect information" sx={{ color: '#94a3b8', fontSize: '0.85rem', cursor: 'help' }} />
                              </Tooltip>
                            </Stack>
                            <Typography sx={{ mt: 0.3, color: impactColor(productContributions.volume), fontSize: '0.9rem', fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
                              {this.formatMoneyDetailed(productContributions.volume)} {reportCurrency}
                            </Typography>
                            <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.7rem', fontWeight: 650, lineHeight: 1.4 }}>
                              Impact from the change in the number of items sold.
                            </Typography>
                          </Paper>
                          <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                            <Stack direction="row" alignItems="center" spacing={0.35}>
                              <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>Comparable-item price effect</Typography>
                              <Tooltip title="The estimated sales impact from price and discount changes only for items found on both days. This is not the overall average sales per item." arrow>
                                <InfoOutlinedIcon aria-label="Comparable-item price effect information" sx={{ color: '#94a3b8', fontSize: '0.85rem', cursor: 'help' }} />
                              </Tooltip>
                            </Stack>
                            <Typography sx={{ mt: 0.3, color: impactColor(productContributions.realized_price), fontSize: '0.9rem', fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
                              {this.formatMoneyDetailed(productContributions.realized_price)} {reportCurrency}
                            </Typography>
                            <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.7rem', fontWeight: 650, lineHeight: 1.4 }}>
                              Impact from realized price and discount changes on comparable items.
                            </Typography>
                          </Paper>
                          <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                            <Stack direction="row" alignItems="center" spacing={0.35}>
                              <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>Item mix effect</Typography>
                              <Tooltip title="The remaining estimated impact from the combination of items sold, including new, discontinued or differently represented items." arrow>
                                <InfoOutlinedIcon aria-label="Item mix effect information" sx={{ color: '#94a3b8', fontSize: '0.85rem', cursor: 'help' }} />
                              </Tooltip>
                            </Stack>
                            <Typography sx={{ mt: 0.3, color: impactColor(productContributions.product_mix), fontSize: '0.9rem', fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
                              {this.formatMoneyDetailed(productContributions.product_mix)} {reportCurrency}
                            </Typography>
                            <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.7rem', fontWeight: 650, lineHeight: 1.4 }}>
                              Remaining impact from the combination and assortment of items sold.
                            </Typography>
                          </Paper>
                        </Box>
                        {itemImpactSummary.length > 0 ? (
                          <Paper variant="outlined" sx={{ mt: 0.8, px: 1, py: 0.8, borderRadius: '10px', borderColor: 'rgba(26, 35, 126, 0.14)', bgcolor: 'rgba(26, 35, 126, 0.035)' }}>
                            <Typography sx={{ color: '#334155', fontSize: '0.7rem', fontWeight: 750, lineHeight: 1.5 }}>
                              {itemImpactSummary.join(' ')}
                            </Typography>
                          </Paper>
                        ) : null}
                        {topProductChanges.length > 0 ? (
                          <Box sx={{ mt: 1 }}>
                            <Typography sx={{ mb: 0.5, color: '#64748b', fontSize: '0.68rem', fontWeight: 850 }}>
                              {isPositive ? 'Biggest item gains' : 'Biggest item declines'}
                            </Typography>
                            <Stack spacing={0.45}>
                              {topProductChanges.map((product) => (
                                <Stack
                                  key={product.product_ref}
                                  direction={{ xs: 'column', sm: 'row' }}
                                  justifyContent="space-between"
                                  spacing={0.25}
                                  sx={{ px: 0.8, py: 0.6, borderRadius: '8px', bgcolor: '#fff' }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ color: '#334155', fontSize: '0.72rem', fontWeight: 800, overflowWrap: 'anywhere' }}>
                                      {product.product_name || product.product_ref}
                                    </Typography>
                                    <Typography sx={{ mt: 0.1, color: '#64748b', fontSize: '0.66rem', fontWeight: 650 }}>
                                      Actual: {this.formatMoneyDetailed(product.actual_sales)} {reportCurrency} • Reference: {this.formatMoneyDetailed(product.reference_sales)} {reportCurrency}
                                    </Typography>
                                  </Box>
                                  <Typography sx={{ color: impactColor(product.difference), fontSize: '0.72rem', fontWeight: 900, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                                    {this.formatMoneyDetailed(product.difference)} {reportCurrency}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        ) : null}
                      </>
                    ) : (
                      <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.74rem', fontWeight: 650 } }}>
                        Item volume, price and mix analysis is not available for this stored calculation.
                      </Alert>
                    )
                  ) : null}
                </Stack>
              </Paper>

              {this.renderSixWeekDevelopment(report.sales_development_context, isPositive)}

              <Paper variant="outlined" sx={{ p: { xs: 1.1, sm: 1.5 }, borderRadius: '14px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                <Typography sx={{ color: '#334155', fontSize: '0.84rem', fontWeight: 900 }}>
                  {decisionHeading}
                </Typography>
                {hasNumericEvidence ? (
                  <>
                    <Typography sx={{ mt: 0.25, color: '#64748b', fontSize: '0.72rem', fontWeight: 650, lineHeight: 1.4 }}>
                      The first three checks decide whether a {isPositive ? 'Sales-up' : 'Sales-down'} alert is created.
                    </Typography>
                    <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 0.75 }}>
                      {this.renderThresholdCheck(
                        'Expected sales level',
                        threshold.expected_gate_passed,
                        `${this.formatMoneyDetailed(result.expected)} ${reportCurrency} expected; minimum ${this.formatMoneyDetailed(threshold.minimum_expected_sales)} ${reportCurrency}.`
                      )}
                      {this.renderThresholdCheck(
                        `Percentage ${changeLabel}`,
                        isSalesValueReport ? threshold.percentage_gate_passed : threshold.decline_gate_passed,
                        `${this.formatPercentMagnitude(result.percentage)} ${changeLabel}; minimum ${this.formatPercentMagnitude(isSalesValueReport ? threshold.minimum_percentage : threshold.decline_threshold_percentage)}.`
                      )}
                      {this.renderThresholdCheck(
                        `Value ${changeLabel}`,
                        isSalesValueReport ? threshold.value_gate_passed : threshold.absolute_loss_gate_passed,
                        `${this.formatMoneyDetailed(Math.abs(Number(result.difference)))} ${reportCurrency} ${changeLabel}; minimum ${this.formatMoneyDetailed(isSalesValueReport ? threshold.minimum_difference : threshold.minimum_absolute_loss)} ${reportCurrency}.`
                      )}
                    </Box>
                    <Alert
                      severity={isAnomaly && threshold.high_severity_gate_passed ? 'warning' : 'info'}
                      icon={false}
                      sx={{ mt: 1, py: 0.2, '& .MuiAlert-message': { fontSize: '0.72rem', fontWeight: 700 } }}
                    >
                      {isAnomaly
                        ? threshold.high_severity_gate_passed
                          ? `Marked High attention because the ${changeLabel} reached ${this.formatPercentMagnitude(threshold.high_severity_percentage)}.`
                          : `Marked Medium attention because the ${this.formatPercentMagnitude(threshold.high_severity_percentage)} high-attention level was not reached.`
                        : 'No alert severity was assigned. The high-attention check changes severity only after all three alert checks pass.'}
                    </Alert>
                    {!driverAvailable && !productAvailable ? (
                      <Alert
                        severity="info"
                        icon={false}
                        sx={{ mt: 0.75, py: 0.2, '& .MuiAlert-message': { fontSize: '0.72rem', fontWeight: 650 } }}
                      >
                        This explains why the system flagged this day. It does not identify what caused sales to {isPositive ? 'rise' : 'fall'}.
                      </Alert>
                    ) : null}
                  </>
                ) : (
                  <Alert severity="warning" sx={{ mt: 1, '& .MuiAlert-message': { fontSize: '0.74rem', fontWeight: 650 } }}>
                    The alert checks were not run because a reliable expected value was not available. Review Evidence quality for the input status.
                  </Alert>
                )}
              </Paper>

              {this.renderReportAccordion(
                'How expected sales were estimated',
                'A simple view of the reference day and the adjustments applied',
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 0.75 }}>
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                    <Typography sx={{ color: '#1a237e', fontSize: '0.7rem', fontWeight: 900 }}>1. Reference day</Typography>
                    <Typography sx={{ mt: 0.4, color: '#111827', fontSize: '0.82rem', fontWeight: 900 }}>
                      {this.formatDate(selectedComparator.observation_date)}
                    </Typography>
                    <Typography sx={{ mt: 0.2, color: '#64748b', fontSize: '0.7rem', fontWeight: 650, lineHeight: 1.4 }}>
                      Sales were {this.formatMoneyDetailed(selectedComparator.value)} {reportCurrency} on the selected comparable day.
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                    <Stack direction="row" alignItems="center" spacing={0.35}>
                      <Typography sx={{ color: '#1a237e', fontSize: '0.7rem', fontWeight: 900 }}>2. Recent trend</Typography>
                      <Tooltip title="Compares recent matching weekdays with the equivalent period one year earlier." arrow>
                        <InfoOutlinedIcon aria-label="Recent trend information" sx={{ color: '#94a3b8', fontSize: '0.85rem', cursor: 'help' }} />
                      </Tooltip>
                    </Stack>
                    <Typography sx={{ mt: 0.4, color: '#111827', fontSize: '0.82rem', fontWeight: 900 }}>
                      {this.formatFactorChange(trend.final_factor)}
                    </Typography>
                    <Typography sx={{ mt: 0.2, color: '#64748b', fontSize: '0.7rem', fontWeight: 650, lineHeight: 1.4 }}>
                      Based on {trend.sample_count || 0} recent matching sales pairs.
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                    <Typography sx={{ color: '#1a237e', fontSize: '0.7rem', fontWeight: 900 }}>3. Monthly context</Typography>
                    <Typography sx={{ mt: 0.4, color: '#111827', fontSize: '0.82rem', fontWeight: 900 }}>
                      {monthly.applied ? this.formatFactorChange(monthly.factor) : 'No monthly adjustment'}
                    </Typography>
                    <Typography sx={{ mt: 0.2, color: '#64748b', fontSize: '0.7rem', fontWeight: 650, lineHeight: 1.4 }}>
                      {monthly.applied
                        ? `Calculated from ${monthly.reference_months_used || 0} reference months.`
                        : 'The available monthly context was not applied to this estimate.'}
                    </Typography>
                  </Paper>
                </Box>,
                true
              )}

              <Paper variant="outlined" sx={{ p: { xs: 1.1, sm: 1.5 }, borderRadius: '14px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                <Typography sx={{ color: '#334155', fontSize: '0.84rem', fontWeight: 900 }}>
                  Evidence quality
                </Typography>
                <Typography sx={{ mt: 0.25, color: '#64748b', fontSize: '0.72rem', fontWeight: 650, lineHeight: 1.4 }}>
                  How complete the information behind this comparison was.
                </Typography>
                <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1 }}>
                  {this.renderReportValue('Evidence quality score', confidenceText, {
                    help: isSalesValueReport
                      ? 'The existing sales-value confidence score, based on comparable-day, trend and monthly evidence. It is not a probability or a source-finality audit.'
                      : 'An evidence-quality score based on comparator coverage, trend samples, monthly data and source completeness. It is not a probability.',
                  })}
                  {this.renderReportValue('Calculated on', this.formatTimestamp(report.generated_at))}
                  {isSalesValueReport ? this.renderReportValue('Trend comparison days', trend.sample_count) : this.renderReportValue(
                    'Source completeness',
                    expectedSources.length === 0
                      ? 'Source list not available'
                      : missingSources.length === 0
                        ? `All ${expectedSources.length} sources complete`
                        : `${missingSources.length} of ${expectedSources.length} sources missing`
                  )}
                  {isSalesValueReport ? this.renderReportValue('Driver comparison', driverAvailable ? 'Available' : 'Unavailable', {
                    help: driverAvailable ? 'Both days have usable sales and transaction counts.' : this.formatCode(driverAnalysis.reason),
                  }) : this.renderReportValue(
                    'Data checks',
                    integrityIssues.length === 0
                      ? 'No issues found'
                      : `${integrityIssues.length} issue${integrityIssues.length === 1 ? '' : 's'} found`,
                    { color: integrityIssues.length === 0 ? '#166534' : '#991b1b' }
                  )}
                </Box>
              </Paper>

              {this.renderReportAccordion(
                'Important to know',
                'What this report can and cannot tell you',
                <Stack spacing={1.25}>
                  <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.74rem', fontWeight: 650 } }}>
                    This is the latest stored calculation for this insight. Recalculating the insight replaces this snapshot; it is not an accounting audit history.
                  </Alert>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}>
                    <Box>
                      <Typography sx={{ mb: 0.6, color: '#334155', fontSize: '0.78rem', fontWeight: 900 }}>Assumptions</Typography>
                      <Stack component="ul" spacing={0.5} sx={{ pl: 2.25, my: 0 }}>
                        {(report.assumptions || []).map((item, index) => (
                          <Typography component="li" key={`assumption-${index}`} sx={{ color: '#475569', fontSize: '0.74rem', lineHeight: 1.45 }}>{item}</Typography>
                        ))}
                      </Stack>
                    </Box>
                    <Box>
                      <Typography sx={{ mb: 0.6, color: '#334155', fontSize: '0.78rem', fontWeight: 900 }}>Limitations</Typography>
                      <Stack component="ul" spacing={0.5} sx={{ pl: 2.25, my: 0 }}>
                        {(report.limitations || []).map((item, index) => (
                          <Typography component="li" key={`limitation-${index}`} sx={{ color: '#475569', fontSize: '0.74rem', lineHeight: 1.45 }}>{item}</Typography>
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              )}

              {this.renderReportAccordion(
                'Technical details',
                'Reason codes, calculation fields, source references, versions and raw data',
                <Stack spacing={1.25}>
                  <Box>
                    <Typography sx={{ mb: 0.6, color: '#334155', fontSize: '0.78rem', fontWeight: 900 }}>Decision codes</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                      {this.renderReportValue('Outcome', this.formatCode(result.outcome))}
                      {this.renderReportValue('Primary reason', this.formatCode(result.primary_reason))}
                    </Box>
                    <Box sx={{ mt: 0.75 }}>{this.renderReportCodes(result.reason_codes)}</Box>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography sx={{ mb: 0.6, color: '#334155', fontSize: '0.78rem', fontWeight: 900 }}>Calculation evidence</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1 }}>
                      {this.renderReportValue('Driver analysis status', this.formatCode(driverAnalysis.status))}
                      {this.renderReportValue('Driver analysis reason', this.formatCode(driverAnalysis.reason))}
                      {this.renderReportValue('Driver comparison basis', this.formatCode(driverAnalysis.comparison_basis))}
                      {this.renderReportValue('Driver method', this.formatCode(driverAnalysis.method))}
                      {this.renderReportValue('Transaction contribution', `${this.formatMoneyDetailed(driverContributions.transactions)} ${reportCurrency}`)}
                      {this.renderReportValue('Average-sale contribution', `${this.formatMoneyDetailed(driverContributions.average_sale_per_transaction)} ${reportCurrency}`)}
                      {this.renderReportValue('Driver source fingerprint', driverAnalysis.source_fingerprint || '-', { monospace: true })}
                      {this.renderReportValue('Product analysis status', this.formatCode(productAnalysis.status))}
                      {this.renderReportValue('Product analysis reason', this.formatCode(productAnalysis.reason))}
                      {this.renderReportValue('Product method', this.formatCode(productAnalysis.method))}
                      {this.renderReportValue('Product volume impact', `${this.formatMoneyDetailed(productContributions.volume)} ${reportCurrency}`)}
                      {this.renderReportValue('Realized-price impact', `${this.formatMoneyDetailed(productContributions.realized_price)} ${reportCurrency}`)}
                      {this.renderReportValue('Product-mix impact', `${this.formatMoneyDetailed(productContributions.product_mix)} ${reportCurrency}`)}
                      {this.renderReportValue('Actual product reconciliation', this.formatBoolean(productActualReconciliation.passed))}
                      {this.renderReportValue('Reference product reconciliation', this.formatBoolean(productReferenceReconciliation.passed))}
                      {this.renderReportValue('Product source fingerprint', productAnalysis.source_fingerprint || '-', { monospace: true })}
                      {this.renderReportValue('Comparator candidates', comparatorDecisions.length)}
                      {this.renderReportValue('Selected reference', selectedComparator.candidate_ref || '-', { monospace: true })}
                      {this.renderReportValue('Calendar kind', this.formatCode(selectedComparator.calendar_kind))}
                      {this.renderReportValue('Comparator state', this.formatCode(selectedComparator.state))}
                      {this.renderReportValue('Trend raw factor', this.formatFactor(trend.raw_factor))}
                      {this.renderReportValue('Trend capped factor', this.formatFactor(trend.normal_capped_factor))}
                      {this.renderReportValue('Trend final factor', this.formatFactor(trend.final_factor))}
                      {this.renderReportValue('Monthly final factor', this.formatFactor(monthly.factor))}
                      {this.renderReportValue('Expected gate', this.formatBoolean(threshold.expected_gate_passed))}
                      {this.renderReportValue('Percentage gate', this.formatBoolean(isSalesValueReport ? threshold.percentage_gate_passed : threshold.decline_gate_passed))}
                      {this.renderReportValue('Value gate', this.formatBoolean(isSalesValueReport ? threshold.value_gate_passed : threshold.absolute_loss_gate_passed))}
                      {this.renderReportValue('High-severity gate', this.formatBoolean(threshold.high_severity_gate_passed))}
                    </Box>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography sx={{ mb: 0.6, color: '#334155', fontSize: '0.78rem', fontWeight: 900 }}>Source evidence</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1 }}>
                      {this.renderReportValue('Target state', this.formatCode(targetEvidence.state))}
                      {this.renderReportValue('Currency state', this.formatCode(currencyEvidence.state))}
                      {this.renderReportValue('Location eligibility', this.formatCode(locationEligibility.state))}
                      {this.renderReportValue('Source as of', this.formatTimestamp(sourceFinality.source_as_of))}
                      {this.renderReportValue('Expected sources', isSalesValueReport ? null : expectedSources.length)}
                      {this.renderReportValue('Finalized sources', isSalesValueReport ? null : finalizedSources.length)}
                      {this.renderReportValue('Missing sources', isSalesValueReport ? null : missingSources.length)}
                      {this.renderReportValue('Recent inactivity', this.formatBoolean(locationEligibility.recent_inactivity))}
                    </Box>
                    <Box sx={{ mt: 0.9 }}>
                      <Typography sx={{ mb: 0.5, color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>Missing source references</Typography>
                      {this.renderReportCodes(missingSources, isSalesValueReport ? 'Not evaluated' : 'None')}
                    </Box>
                    <Box sx={{ mt: 0.9 }}>
                      <Typography sx={{ mb: 0.5, color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>Integrity issues</Typography>
                      {this.renderReportCodes(integrityIssues, isSalesValueReport ? 'Not evaluated' : 'None recorded')}
                    </Box>
                    {sources.map((source, index) => (
                      <Paper key={`${source.source_ref || 'source'}-${index}`} variant="outlined" sx={{ mt: 0.75, p: 1, borderRadius: '10px', borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#fff' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5}>
                          <Typography sx={{ color: '#111827', fontSize: '0.76rem', fontWeight: 900, overflowWrap: 'anywhere' }}>{source.source_ref || '-'}</Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 750 }}>{this.formatCode(source.state)} • {this.formatTimestamp(source.finalized_at)}</Typography>
                        </Stack>
                        {(source.integrity_issues || []).length > 0 ? <Box sx={{ mt: 0.6 }}>{this.renderReportCodes(source.integrity_issues)}</Box> : null}
                      </Paper>
                    ))}
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    {this.renderReportValue('Generated at', this.formatTimestamp(report.generated_at))}
                    {this.renderReportValue('Data treated as final through', this.formatDate(report.final_through_date))}
                    {this.renderReportValue('Report schema', report.schema_version || '-', { monospace: true })}
                    {this.renderReportValue('Evidence profile', report.evidence_profile || '-', { monospace: true })}
                    {this.renderReportValue('Algorithm version', report.algorithm_version || result.algorithm_version || '-', { monospace: true })}
                    {this.renderReportValue('Evidence version', report.evidence_version || result.evidence_version || '-', { monospace: true })}
                    {this.renderReportValue('Policy version', report.policy_version || result.policy_version || '-', { monospace: true })}
                    {this.renderReportValue('Period rule', result.period_rule_version || '-', { monospace: true })}
                    {this.renderReportValue('Source fingerprint', report.source_fingerprint || '-', { monospace: true })}
                    {this.renderReportValue('Report hash', report.report_hash || '-', { monospace: true })}
                  </Box>
                  <Box
                    component="pre"
                    aria-label="Complete sales report payload"
                    sx={{
                      m: 0,
                      p: 1,
                      maxHeight: 360,
                      overflow: 'auto',
                      borderRadius: '10px',
                      color: '#334155',
                      bgcolor: '#eef2f7',
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      fontSize: '0.68rem',
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {JSON.stringify(report, null, 2)}
                  </Box>
                </Stack>
              )}
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: { xs: 1.25, sm: 2 }, py: 1, bgcolor: '#fff', borderTop: '1px solid rgba(15, 23, 42, 0.08)' }}>
          {this.state.salesDownReportError ? (
            <Button onClick={this.loadSalesDownReport} disabled={this.state.salesDownReportLoading} sx={{ textTransform: 'none', fontWeight: 850 }}>
              Retry
            </Button>
          ) : null}
          <Button onClick={this.closeSalesDownReport} sx={{ textTransform: 'none', fontWeight: 850 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const page = this.state.page;
    const rowsPerPage = this.state.rowsPerPage;
    const sortedInsightsList = [...(this.state.insights || [])].sort((a, b) => {
      const dateDiff = new Date(b.period_start || 0) - new Date(a.period_start || 0);
      if (dateDiff !== 0) {
        return dateDiff;
      }
      const summaryDiff = Number(this.isDailySummary(b)) - Number(this.isDailySummary(a));
      if (summaryDiff !== 0) {
        return summaryDiff;
      }
      const severityDiff = Number(b.severity || 0) - Number(a.severity || 0);
      if (severityDiff !== 0) {
        return severityDiff;
      }
      return Math.abs(Number(b.difference_percent || 0)) - Math.abs(Number(a.difference_percent || 0));
    });

    const insightsList = sortedInsightsList.filter((row) => {
      const status = Number(row.status);

      if (this.state.statusView === "open") {
        return [0, 1, 2].includes(status);
      }
      if (this.state.statusView === "resolved") {
        return status === 3;
      }
      if (this.state.statusView === "ignored") {
        return status === 4;
      }
      if (this.state.statusView === "archived") {
        return status === 9;
      }

      return true;
    });

    const criticalHighCount = insightsList.filter((row) => Number(row.severity) >= 3).length;
    const openCount = sortedInsightsList.filter((row) => [0, 1, 2].includes(Number(row.status))).length;
    const summaryCount = insightsList.filter((row) => this.isDailySummary(row)).length;
    const attentionCount = insightsList.filter((row) => !this.isDailySummary(row) && Number(row.difference_percent || 0) < 0).length;
    const opportunityCount = insightsList.filter((row) => !this.isDailySummary(row) && Number(row.difference_percent || 0) > 0).length;

    const visibleRows = rowsPerPage > 0
      ? insightsList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : insightsList;

    const controlSx = {
      minWidth: 0,
      '& .MuiInputBase-root': {
        height: 38,
        fontSize: { xs: 12.5, sm: 13.5 },
        bgcolor: '#fff',
        borderRadius: 1.5,
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: 12.5, sm: 13.5 },
      },
    };

    const paginationSx = {
      flexShrink: 0,
      borderTop: '1px solid rgba(15, 23, 42, 0.06)',
      bgcolor: '#fff',
      minHeight: { xs: 44, sm: 50 },
      '& .MuiTablePagination-toolbar': {
        minHeight: { xs: 44, sm: 50 },
        px: { xs: 0.5, sm: 1 },
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: 0.25, sm: 0.5 },
      },
      '& .MuiTablePagination-spacer': {
        display: { xs: 'none', sm: 'block' },
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: { xs: '0.76rem', sm: '0.86rem' },
        m: 0,
      },
      '& .MuiTablePagination-actions': {
        ml: { xs: 0, sm: 1 },
      },
    };

    const isSingleDay = this.state.dateFrom && this.state.dateFrom === this.state.dateTo;
    const periodText = isSingleDay
      ? `Insights for ${this.formatDate(this.state.dateFrom)}`
      : `Insights from ${this.formatDate(this.state.dateFrom)} to ${this.formatDate(this.state.dateTo)}`;

    return (
      <Paper
        sx={{
          width: '100%',
          height: { xs: 650, sm: '100%' },
          maxHeight: { xs: 650, sm: '100%' },
          minHeight: 0,
          borderRadius: { xs: '14px', sm: '16px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'none',
          border: 0,
          bgcolor: '#fff',
          containerType: 'inline-size',
        }}
      >
        <Box
          sx={{
            px: { xs: 1, sm: 1.25 },
            py: { xs: 0.9, sm: 1 },
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#1a237e',
                fontWeight: 950,
                fontSize: { xs: '1rem', sm: '1.05rem' },
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Intelligence inbox
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                display: { xs: 'none', sm: 'block' },
                color: '#64748b',
                fontWeight: 650,
                fontSize: '0.76rem',
                lineHeight: 1.25,
              }}
            >
              {periodText} · includes ongoing transaction warnings
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ flexShrink: 0, flexWrap: 'wrap' }}
          >
            <Chip
              label={`${insightsList.length} total`}
              size="small"
              sx={{
                height: 24,
                color: '#1a237e',
                bgcolor: 'rgba(26, 35, 126, 0.08)',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                fontWeight: 850,
                fontSize: '0.7rem',
              }}
            />

            <Chip
              label={`${criticalHighCount} critical/high`}
              size="small"
              sx={{
                height: 24,
                color: '#991b1b',
                bgcolor: 'rgba(239, 68, 68, 0.10)',
                border: '1px solid rgba(239, 68, 68, 0.18)',
                fontWeight: 850,
                fontSize: '0.7rem',
              }}
            />
            <Chip
              label={`${openCount} open`}
              size="small"
              sx={{
                height: 24,
                color: '#075985',
                bgcolor: 'rgba(14, 165, 233, 0.10)',
                border: '1px solid rgba(14, 165, 233, 0.18)',
                fontWeight: 850,
                fontSize: '0.7rem',
              }}
            />

          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            mt: 0.75,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Chip
            label="Open"
            size="small"
            onClick={() => this.handleStatusViewChange("open")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "open" ? '#1a237e' : '#334155',
              bgcolor: this.state.statusView === "open"
                ? 'rgba(26, 35, 126, 0.12)'
                : '#f8fafc',
              border: this.state.statusView === "open"
                ? '1px solid rgba(26, 35, 126, 0.20)'
                : '1px solid rgba(15, 23, 42, 0.10)',
              '&:hover': {
                bgcolor: 'rgba(26, 35, 126, 0.10)',
              },
            }}
          />

          <Chip
            label="Resolved"
            size="small"
            onClick={() => this.handleStatusViewChange("resolved")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "resolved" ? '#166534' : '#334155',
              bgcolor: this.state.statusView === "resolved"
                ? 'rgba(34, 197, 94, 0.12)'
                : '#f8fafc',
              border: this.state.statusView === "resolved"
                ? '1px solid rgba(34, 197, 94, 0.22)'
                : '1px solid rgba(15, 23, 42, 0.10)',
              '&:hover': {
                bgcolor: 'rgba(34, 197, 94, 0.08)',
              },
            }}
          />

          <Chip
            label="Ignored"
            size="small"
            onClick={() => this.handleStatusViewChange("ignored")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "ignored" ? '#475569' : '#334155',
              bgcolor: this.state.statusView === "ignored"
                ? 'rgba(100, 116, 139, 0.14)'
                : '#f8fafc',
              border: '1px solid rgba(100, 116, 139, 0.16)',
              '&:hover': {
                bgcolor: 'rgba(100, 116, 139, 0.10)',
              },
            }}
          />

          <Chip
            label="Archived"
            size="small"
            onClick={() => this.handleStatusViewChange("archived")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "archived" ? '#475569' : '#334155',
              bgcolor: this.state.statusView === "archived"
                ? 'rgba(100, 116, 139, 0.14)'
                : '#f8fafc',
              border: '1px solid rgba(100, 116, 139, 0.16)',
              '&:hover': {
                bgcolor: 'rgba(100, 116, 139, 0.10)',
              },
            }}
          />

          <Chip
            label="All"
            size="small"
            onClick={() => this.handleStatusViewChange("all")}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: this.state.statusView === "all" ? '#1a237e' : '#334155',
              bgcolor: this.state.statusView === "all"
                ? 'rgba(26, 35, 126, 0.10)'
                : '#f8fafc',
              border: '1px solid rgba(15, 23, 42, 0.10)',
              '&:hover': {
                bgcolor: '#eef2ff',
              },
            }}
          />

          {summaryCount > 0 ? (
            <Chip
              label={`${summaryCount} summary`}
              size="small"
              sx={{
                height: 24,
                color: '#1a237e',
                bgcolor: 'rgba(26, 35, 126, 0.06)',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                fontWeight: 850,
                fontSize: '0.7rem',
              }}
            />
          ) : null}
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            px: 0.75,
            py: 0.6,
            display: 'flex',
            '@container (min-width: 900px)': {
              display: 'none',
            },
            alignItems: 'center',
            flexWrap: 'wrap',
            borderBottom: this.state.showMobileFilters
              ? 0
              : '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#fff',
          }}
        >
          <Chip
            label={this.state.showMobileFilters ? 'Hide filters' : 'Filters'}
            size="small"
            icon={this.state.showMobileFilters ? <ExpandLessIcon /> : <TuneIcon />}
            onClick={() =>
              this.setState((prev) => ({
                showMobileFilters: !prev.showMobileFilters,
              }))
            }
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: '#1a237e',
              bgcolor: this.state.showMobileFilters
                ? 'rgba(26, 35, 126, 0.12)'
                : 'rgba(26, 35, 126, 0.06)',
              border: '1px solid rgba(26, 35, 126, 0.18)',
              '& .MuiChip-icon': {
                color: '#1a237e',
                fontSize: '0.95rem',
              },
            }}
          />

          <Chip
            label={this.state.dateFrom === this.state.dateTo
              ? this.formatDate(this.state.dateFrom)
              : `${this.formatDate(this.state.dateFrom)} - ${this.formatDate(this.state.dateTo)}`
            }
            size="small"
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 850,
              color: '#334155',
              bgcolor: '#f8fafc',
              border: '1px solid rgba(15, 23, 42, 0.10)',
            }}
          />

          {this.state.severity !== "" ? (
            <Chip
              label={`Severity ${this.state.severity}`}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#334155',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.10)',
              }}
            />
          ) : null}

          {this.state.insightType !== "" ? (
            <Chip
              label={`Type ${this.state.insightType}`}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#334155',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.10)',
              }}
            />
          ) : null}
        </Stack>

        <Box
          sx={{
            px: { xs: 0.75, sm: 1 },
            py: { xs: 0.75, sm: 0.85 },
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#fff',
            display: this.state.showMobileFilters ? 'block' : 'none',
            '@container (min-width: 900px)': {
              display: 'block',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 0.55, sm: 0.65 },
              alignItems: 'center',
              '& > .MuiTextField-root': {
                flex: '0 0 auto',
                minWidth: 0,
              },
              '& > .MuiTextField-root:nth-of-type(1), & > .MuiTextField-root:nth-of-type(2)': {
                width: { xs: 'calc((100% - 8px) / 2)', sm: 130, lg: 140 },
              },
              '& > .MuiTextField-root:nth-of-type(3)': {
                width: { xs: 'calc((100% - 8px) / 2)', sm: 110, lg: 115 },
              },
              '& > .MuiTextField-root:nth-of-type(4)': {
                width: { xs: 'calc((100% - 8px) / 2)', sm: 120, lg: 125 },
              },
              '& > .MuiTextField-root:nth-of-type(5)': {
                width: { xs: 'calc((100% - 8px) / 2)', sm: 110, lg: 115 },
              },
            }}
          >
            <TextField
              size="small"
              type="date"
              label="From"
              name="dateFrom"
              value={this.state.dateFrom}
              onChange={this.handleFilterChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...controlSx,
                '& input': {
                  px: { xs: 0.55, sm: 0.75 },
                  fontSize: { xs: 13.5, sm: 14 },
                  letterSpacing: { xs: -0.4, sm: -0.25 },
                },
              }}
            />

            <TextField
              size="small"
              type="date"
              label="To"
              name="dateTo"
              value={this.state.dateTo}
              onChange={this.handleFilterChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...controlSx,
                '& input': {
                  px: { xs: 0.55, sm: 0.75 },
                  fontSize: { xs: 13.5, sm: 14 },
                  letterSpacing: { xs: -0.4, sm: -0.25 },
                },
              }}
            />

            <TextField
              size="small"
              select
              label="Severity"
              name="severity"
              value={this.state.severity}
              onChange={this.handleFilterChange}
              sx={controlSx}
            >
              <MenuItem value="">Severity</MenuItem>
              <MenuItem value="1">Low</MenuItem>
              <MenuItem value="2">Medium</MenuItem>
              <MenuItem value="3">High</MenuItem>
              <MenuItem value="4">Critical</MenuItem>
            </TextField>

            <TextField
              size="small"
              select
              label="Status"
              name="status"
              value={this.state.status}
              onChange={this.handleFilterChange}
              sx={controlSx}
            >
              <MenuItem value="">Status</MenuItem>
              <MenuItem value="0">New</MenuItem>
              <MenuItem value="1">Seen</MenuItem>
              <MenuItem value="2">In Review</MenuItem>
              <MenuItem value="3">Resolved</MenuItem>
              <MenuItem value="4">Ignored</MenuItem>
              <MenuItem value="9">Archived</MenuItem>
            </TextField>

            <TextField
              size="small"
              select
              label="Type"
              name="insightType"
              value={this.state.insightType}
              onChange={this.handleFilterChange}
              sx={controlSx}
            >
              <MenuItem value="">Type</MenuItem>
              <MenuItem value="1001">Below</MenuItem>
              <MenuItem value="1002">Above</MenuItem>
              <MenuItem value="1003">Summary</MenuItem>
              <MenuItem value="1011">Transactions</MenuItem>
            </TextField>

            <Tooltip title="Reset filters" arrow>
              <IconButton
                size="small"
                onClick={this.clearFilters}
                sx={{
                  width: { xs: 'calc((100% - 8px) / 2)', sm: 38 },
                  flex: '0 0 auto',
                  height: { xs: 38, sm: 38 },
                  borderRadius: 1.5,
                  color: '#1a237e',
                  border: '1px solid rgba(26, 35, 126, 0.24)',
                  bgcolor: '#fff',
                  justifySelf: 'stretch',
                  '&:hover': {
                    borderColor: 'rgba(26, 35, 126, 0.42)',
                    bgcolor: 'rgba(26, 35, 126, 0.04)',
                  },
                }}
              >
                <RestartAltIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              mt: 0.75,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Chip
              label="Yesterday"
              size="small"
              onClick={() => this.applyDatePreset("yesterday")}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#1a237e',
                bgcolor: this.state.dateFrom === getLocalDateString(addDays(new Date(), -1)) &&
                  this.state.dateTo === getLocalDateString(addDays(new Date(), -1))
                  ? 'rgba(26, 35, 126, 0.12)'
                  : 'rgba(26, 35, 126, 0.05)',
                border: '1px solid rgba(26, 35, 126, 0.16)',
                '&:hover': {
                  bgcolor: 'rgba(26, 35, 126, 0.10)',
                },
              }}
            />

            <Chip
              label="Today"
              size="small"
              onClick={() => this.applyDatePreset("today")}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#334155',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.10)',
                '&:hover': {
                  bgcolor: '#eef2ff',
                },
              }}
            />

            <Chip
              label="Last 7 days"
              size="small"
              onClick={() => this.applyDatePreset("last7")}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 850,
                color: '#334155',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.10)',
                '&:hover': {
                  bgcolor: '#eef2ff',
                },
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              mt: 0.75,
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
            }}
          >
            <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 750 }}>
              Attention: {attentionCount}
            </Typography>
            <Typography sx={{ color: '#cbd5e1', fontSize: '0.72rem' }}>•</Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 750 }}>
              Opportunity: {opportunityCount}
            </Typography>
          </Stack>
        </Box>

        <Box
          ref={this.listRef}
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'auto',
            p: { xs: 0.75, sm: 1 },
            bgcolor: '#f8fafc',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 0.6, md: 0.7 },
            '&::-webkit-scrollbar': {
              height: 8,
              width: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(15, 23, 42, 0.18)',
              borderRadius: 999,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(15, 23, 42, 0.04)',
            },
          }}
        >
          {visibleRows.map((row) => this.renderInsightCard(row))}

          {insightsList.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: '14px',
                textAlign: 'center',
                color: 'text.secondary',
                borderColor: 'rgba(15, 23, 42, 0.08)',
                bgcolor: '#fff',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#334155' }}>
                No insights found
              </Typography>

              <Typography sx={{ mt: 0.4, fontWeight: 500, fontSize: '0.8rem', color: '#64748b' }}>
                There are no insights or summaries matching the selected filters.
              </Typography>
            </Paper>
          ) : null}
        </Box>

        <TablePagination
          rowsPerPageOptions={[5, 8, 12, { value: -1, label: 'All' }]}
          labelRowsPerPage="Rows:"
          component="div"
          count={insightsList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={this.handleChangePage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          sx={paginationSx}
        />

        {this.renderInsightDrawer()}
        {this.renderSalesDownReportModal()}

        <Snackbar
          open={this.state.snackbarOpen}
          autoHideDuration={3000}
          onClose={this.closeSnackbar}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <Alert
            onClose={this.closeSnackbar}
            severity={this.state.snackbarSeverity}
            variant="filled"
            sx={{
              width: '100%',
              fontWeight: 800,
            }}
          >
            {this.state.snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    );
  }
}

export default IntelligenceInsights;
